import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Button from "components/button";
import Card from "components/card";
import { useToast } from "components/Toast";
import {
  adminGetBusiness,
  adminUpdateBusinessStatus,
  deleteBusiness,
} from "services/businessService";
import { getStatusBadgeColor, formatStatusLabel } from "utils/businessUtils";
import { Business } from "types";

const AdminBusinessShow: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [business, setBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState(false);
  const toast = useToast();

  useEffect(() => {
    // Fetch business from API
    const fetchBusiness = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const business = await adminGetBusiness(Number(id));
        setBusiness(business);
      } catch (err: any) {
        console.error("Error loading business:", err);
        const message =
          err?.response?.data?.message || "Failed to load business details.";
        toast.showError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchBusiness();
  }, [id, toast]);

  const handleApprove = async () => {
    if (!business) return;
    setActioning(true);
    try {
      await adminUpdateBusinessStatus(business.id, "approved");
      setBusiness({ ...business, status: "approved" });
      toast.showSuccess("Business approved successfully");
    } catch (err: any) {
      toast.showError("Failed to approve business");
      console.error(err);
    } finally {
      setActioning(false);
    }
  };

  const handleReject = async () => {
    if (!business) return;
    setActioning(true);
    try {
      await adminUpdateBusinessStatus(business.id, "rejected");
      setBusiness({ ...business, status: "rejected" });
      toast.showSuccess("Business rejected successfully");
    } catch (err: any) {
      toast.showError("Failed to reject business");
      console.error(err);
    } finally {
      setActioning(false);
    }
  };

  const handleDelete = async () => {
    if (
      !business ||
      !window.confirm("Are you sure you want to delete this business?")
    )
      return;
    setActioning(true);
    try {
      await deleteBusiness(business.id);
      toast.showSuccess("Business deleted successfully");
      navigate("/admin/businesses");
    } catch (err: any) {
      toast.showError("Failed to delete business");
      console.error(err);
    } finally {
      setActioning(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4">
        <Card extra="w-full p-4">
          <p>Loading business details...</p>
        </Card>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="p-4">
        <Card extra="w-full p-4">
          <p>Business not found.</p>
          <Link
            to="/admin/businesses"
            className="mt-4 text-blue-500 hover:underline"
          >
            Back to Businesses
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-white">
          Business Details
        </h1>
        <Link
          to="/admin/businesses"
          className="text-blue-500 hover:underline dark:text-blue-400"
        >
          ← Back to Businesses
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Main Details */}
        <div className="lg:col-span-2">
          <Card extra="w-full p-6">
            <div className="mb-6">
              <h2 className="mb-2 text-2xl font-bold text-gray-800 dark:text-white">
                {business.name}
              </h2>
              <p
                className={`inline-block rounded-full px-4 py-1 text-sm font-semibold ${getStatusBadgeColor(
                  business.status
                )}`}
              >
                {formatStatusLabel(business.status)}
              </p>
            </div>

            <div className="mb-6">
              <h3 className="mb-2 font-semibold text-gray-700 dark:text-gray-300">
                Category
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {business.category}
              </p>
            </div>

            <div className="mb-6">
              <h3 className="mb-2 font-semibold text-gray-700 dark:text-gray-300">
                Description
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {business.description}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {business.phone && (
                <div>
                  <h3 className="mb-1 font-semibold text-gray-700 dark:text-gray-300">
                    Phone
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {business.phone}
                  </p>
                </div>
              )}
              {business.email && (
                <div>
                  <h3 className="mb-1 font-semibold text-gray-700 dark:text-gray-300">
                    Email
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {business.email}
                  </p>
                </div>
              )}
              {business.website && (
                <div>
                  <h3 className="mb-1 font-semibold text-gray-700 dark:text-gray-300">
                    Website
                  </h3>
                  <a
                    href={business.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:underline dark:text-blue-400"
                  >
                    {business.website}
                  </a>
                </div>
              )}
              {business.address && (
                <div>
                  <h3 className="mb-1 font-semibold text-gray-700 dark:text-gray-300">
                    Address
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {business.address}
                  </p>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Owner & Actions */}
        <div className="flex flex-col gap-4">
          <Card extra="w-full p-6">
            <h3 className="mb-4 font-bold text-gray-800 dark:text-white">
              Business Owner
            </h3>
            <div className="mb-4 rounded bg-gray-50 p-4 dark:bg-gray-900/30">
              <p className="mb-1 text-sm font-semibold text-gray-700 dark:text-gray-300">
                Name
              </p>
              <p className="mb-3 text-gray-600 dark:text-gray-400">
                {business.user.firstname} {business.user.lastname}
              </p>
              <p className="mb-1 text-sm font-semibold text-gray-700 dark:text-gray-300">
                Email
              </p>
              <p className="mb-3 text-gray-600 dark:text-gray-400">
                {business.user.email}
              </p>
              {business.user.phone && (
                <>
                  <p className="mb-1 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Phone
                  </p>
                  <p className="text-gray-600 dark:text-gray-400">
                    {business.user.phone}
                  </p>
                </>
              )}
            </div>
          </Card>

          <Card extra="w-full p-6">
            <h3 className="mb-4 font-bold text-gray-800 dark:text-white">
              Actions
            </h3>
            <div className="flex flex-col gap-2">
              {business.status !== "approved" && (
                <Button
                  onClick={handleApprove}
                  disabled={actioning}
                  className="w-full"
                >
                  {actioning ? "Processing..." : "✓ Approve"}
                </Button>
              )}
              {business.status !== "rejected" && (
                <Button
                  onClick={handleReject}
                  disabled={actioning}
                  variant="outline"
                  className="w-full"
                >
                  {actioning ? "Processing..." : "✗ Reject"}
                </Button>
              )}
              <Button
                onClick={handleDelete}
                disabled={actioning}
                variant="danger"
                className="w-full"
              >
                {actioning ? "Processing..." : "Delete"}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminBusinessShow;
